/**
 * Printing without a pop-up.
 *
 * Both print paths in the app used `window.open('', '_blank')` and wrote a
 * document into the new window. Browsers treat that as a pop-up and block it by
 * default, so pressing Print produced "Pop-up blocked — check browser settings"
 * and nothing printed. The block is not something the page can ask its way out
 * of; the user has to whitelist the site, which is a poor answer for a button
 * that should just print.
 *
 * A same-document hidden iframe needs no pop-up permission: it is an ordinary
 * element, and printing it prints only its own content, so the surrounding app
 * chrome stays out of the output without needing @media print rules.
 */

import { showToast } from '@/ui/overlays';

const esc = (s: string): string =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Print `text` as a plain monospaced document titled `title`.
 *
 * Returns true if the print dialog was invoked. Callers do not need to handle
 * false — the user has already been told — but it is there for tests.
 */
export function printPlainText(title: string, text: string): boolean {
  const html =
    '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>' +
    esc(title) +
    '</title><style>' +
    'body{font-family:"Courier New",monospace;font-size:11pt;color:#111;background:#fff;margin:32px 40px;line-height:1.8;}' +
    'h1{font-family:Arial,Helvetica,sans-serif;font-size:14pt;border-bottom:2px solid #333;padding-bottom:6px;margin-bottom:16px;}' +
    'pre{font-family:inherit;white-space:pre-wrap;word-wrap:break-word;margin:0;}' +
    '@media print{body{margin:16px 20px;}}' +
    '</style></head><body><h1>' +
    esc(title) +
    '</h1><pre>' +
    esc(text) +
    '</pre></body></html>';

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  // Off-screen rather than display:none — a display:none iframe does not lay
  // out, and some engines then print a blank page.
  frame.style.cssText =
    'position:fixed;left:-9999px;top:0;width:816px;height:1056px;border:0;visibility:hidden;';
  document.body.appendChild(frame);

  const done = (): void => {
    // Deferred: removing the iframe while the print dialog is still reading it
    // cancels the job in some browsers.
    setTimeout(() => frame.remove(), 60_000);
  };

  try {
    const doc = frame.contentDocument;
    if (!doc) throw new Error('no iframe document');
    doc.open();
    doc.write(html);
    doc.close();

    const run = (): void => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch {
        showToast('Could not open the print dialog — use Save instead');
      }
      done();
    };

    // Wait for the written document to finish laying out; onload fires for
    // document.write content once close() completes.
    if (doc.readyState === 'complete') run();
    else frame.onload = run;
    return true;
  } catch {
    frame.remove();
    showToast('Could not open the print dialog — use Save instead');
    return false;
  }
}
